import type { Command } from 'common/Prose/Commands'
import { times } from 'lodash'
import { schema } from 'prose-core'
import { joinForward } from 'prosemirror-commands'
import { NodeSelection, Selection, TextSelection } from 'prosemirror-state'
import { setTrackedChangeCommand } from '../Plugins/TrackChanges/commands'
import { CommandName } from '../Plugins/TrackChanges/commands/types'
import { combineCommands } from '../utils/combineCommands'

/**
 * If the selection is empty and the cursor is at the end of a textblock, try to pull the next item up and merge it with
 * the current textblock
 */
export const pullNextItem: Command = (state, dispatch) => {
  const { selection, tr } = state

  if (!(selection instanceof TextSelection)) {
    return false // not text selection
  }

  if (!selection.$from.node().type.groups.includes('textWrap')) {
    return false // current node is not textual
  }

  if (selection.from !== selection.$from.end()) {
    return false // not a cursor
  }

  const maybeNextNodeSelection = Selection.near(state.doc.resolve(selection.from + 1))
  if (!(maybeNextNodeSelection instanceof NodeSelection)) {
    const combinedTr = combineCommands(
      state,
      times(selection.$from.depth, () => joinForward),
    )

    if (combinedTr) {
      // Our current handling on trackTransaction erases two chars instead of pulling the next line
      setTrackedChangeCommand(combinedTr, {
        name: CommandName.PULL_NEXT_ITEM,
        meta: { targetNodePos: maybeNextNodeSelection.to },
      })
      dispatch?.(combinedTr)
      return true
    }
  } else {
    // the node after this is a leaf node (e.g. an image)
    if (selection.$from.node(-1).type === schema.nodes.list_item) {
      // for list items, we pull the node into the LI
      const nodeToInsert = maybeNextNodeSelection.node
      tr.deleteRange(maybeNextNodeSelection.from, maybeNextNodeSelection.from + 1)
      tr.insert(selection.from, nodeToInsert)
      dispatch?.(tr)
      return true
    } else {
      // otherwise just select the node
      dispatch?.(tr.setSelection(maybeNextNodeSelection))
      return true
    }
  }

  return false
}
